import { consumeAuthDenialMessage } from '@Core/Auth/authDenial'
import { isAuthFlagEnabled } from '@Core/Auth/authFlags'
import { CookiesStorage } from '@Core/Communication/GlobalStore/CookiesStorage/CookiesStorage'
import { useAuth } from '@Core/Hooks/useAuth'
import { OAuthProvider } from 'firebase/auth'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { loginTestID } from './Login.constants'
import { Actions, TextButton } from './Login.styles'
import { BadgeFace, LoginBadge } from './LoginBadge'
import { LoginForm } from './LoginForm'
import { MicrosoftLoginButton } from './MicrosoftLoginButton'

/**
 * Login screen. During the transition Microsoft is on the front of the badge
 * and the e-mail and password form is on its back, reached by turning the
 * card over; once `auth_password_restricted` is on, only Microsoft is offered
 * here — authorized exceptions use /login/alternativo (contracts/ui-auth.md §1).
 */
export const Login = () => {
    const { authenticate, uiAuthenticate, loading } = useAuth(
        new CookiesStorage(),
        new OAuthProvider('microsoft.com')
    )
    const passwordRestricted = isAuthFlagEnabled('passwordRestricted')
    const showMicrosoft = passwordRestricted || isAuthFlagEnabled('msLoginEnabled')
    const showPassword = !passwordRestricted

    const [flipped, setFlipped] = useState(false)
    // The back is mounted on the first turn and kept so it can turn back.
    const [backMounted, setBackMounted] = useState(false)
    const backRef = useRef<HTMLDivElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const denial = consumeAuthDenialMessage()
        if (denial) toast.error(denial)
    }, [])

    useEffect(() => {
        if (flipped) backRef.current?.querySelector('input')?.focus()
    }, [flipped, backMounted])

    const turnOver = () => {
        setBackMounted(true)
        setFlipped(true)
    }

    const turnBack = () => {
        setFlipped(false)
        toggleRef.current?.focus()
    }

    // Before the rollout the form is the only way in: no Microsoft, no back.
    if (!showMicrosoft) {
        return (
            <LoginBadge>
                <BadgeFace title="Bem-vindo(a)" subtitle="Entre com seu e-mail e senha">
                    <Actions>
                        <LoginForm uiAuthenticate={uiAuthenticate} loading={loading} />
                    </Actions>
                </BadgeFace>
            </LoginBadge>
        )
    }

    // The card keeps its two faces from the start so the first turn animates;
    // the form itself only mounts when someone asks for it.
    const back = !showPassword ? undefined : backMounted ? (
        <BadgeFace title="Entrar com e-mail e senha" compact>
            <Actions ref={backRef}>
                <LoginForm uiAuthenticate={uiAuthenticate} loading={loading} />
                <TextButton type="button" onClick={turnBack}>
                    Voltar para Microsoft
                </TextButton>
            </Actions>
        </BadgeFace>
    ) : (
        <></>
    )

    return (
        <LoginBadge back={back} flipped={flipped}>
            <BadgeFace title="Bem-vindo(a)" subtitle="Entre com sua conta corporativa">
                <Actions>
                    <MicrosoftLoginButton onClick={authenticate} loading={loading} />
                    {showPassword && (
                        <TextButton
                            ref={toggleRef}
                            type="button"
                            data-testid={loginTestID.passwordToggle}
                            onClick={turnOver}
                        >
                            Entrar com e-mail e senha
                        </TextButton>
                    )}
                </Actions>
            </BadgeFace>
        </LoginBadge>
    )
}
